import React, { useState, useCallback, useEffect } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Formik, Form, Field } from 'formik'
import { useModal } from 'react-modal-hook'
import {
  LOAN_KEYS,
  DIALOG_KEYS,
  VALIDATION_MESSAGES,
  SIDENAV_KEYS,
  PAYDAY_LOANS_KEYS,
  DEPOSITS_KEYS,
} from '../../../utils/translation-constants'
import NeoSuccess from '../../../components/FormComponents/NeoSuccess'
import NeoButton from '../../../components/NeoButton/NeoButton'
import Loading from 'custom/assets/loaders/Loading'
import Modal from '../../../components/Modal/Modal'
import NeoInput from '../../../components/NeoInput/NeoInput'
import InputError from '../../../components/InputError/InputError'
import LoanService from '../../../services/loan.service'
import AccountsService from '../../../services/accounts.service'
import NeoOTP from '../../../components/FormComponents/NeoOTP'
import { getFeatureListSelector } from '../../../selectors'
import NumberFormat from 'react-number-format'

import LayoutWithOutAccountCard from '../../../layout/Layout/layout-without-card'

import { LoanRepaymentSchema } from '../../../schemas/loan'
import TermsAndConditions from '../../../components/TermsAndConditions/TermsAndCondition'

import validationConfig from 'custom/config/validation.config.json'
const loanConfig = validationConfig['loan']

export function LoanRepayment() {
  const { t } = useTranslation()
  const location = useLocation()
  const [stage, setStage] = useState(0)
  const [message, setMessage] = useState('Message Placeholder')
  const [formValues, setFormValues] = useState({})
  const [loanFormValues, setLoanFormValues] = useState({})
  const [loading, setLoading] = useState(false)
  const [loanAccountList, setLoanAccountList] = useState([])
  const [accountList, setAccountList] = useState([])
  const history = useHistory()
  const [randomKey, setRandomKey] = useState('')
  const [otp, setOTP] = useState('')
  const [showAmount, setShowAmount] = useState(false)
  const [totalLoanAmount, setTotalLoanAmount] = useState('')
  const featureList = useSelector(getFeatureListSelector)

  const [showModal, hideModal] = useModal(
    () => (
      <Modal>
        <div className='modal-error-body'>
          <h6>
            <b>{message?.code}</b> : {message?.message}
          </h6>
          <div className='modal-btn-style'>
            <NeoButton
              mode='newDesign'
              type='button'
              text={t(DIALOG_KEYS.CLOSE)}
              onClick={() => {
                hideModal()
              }}
            />
          </div>
        </div>
      </Modal>
    ),
    [message],
  )

  const fetchAccounts = useCallback(async () => {
    const response = await AccountsService.getAllAccounts()
    if (response.status === 200) {
      const casaAccountList = response.data['accountSummaryDetails']
      setAccountList(casaAccountList)
      setStage(1)
    }
  }, [])

  useEffect(() => {
    if (location.state === undefined) {
      history.push('/loans')
      return
    }
    fetchAccounts()
    setLoanAccountList(location.state.loanAccounts)
    // fetchAccounts()
  }, [])

  const verifyLoanDetailsCallback = useCallback(
    async (data) => {
      setLoanFormValues(data)
      if (data.paymentMode === 'PARTIAL' && +data.amount > totalLoanAmount) {
        setMessage({
          code: 'JS Error',
          message: `Enter amount less than ${totalLoanAmount}`,
        })
        showModal()
        return
      }
      setStage(2)
    },
    [totalLoanAmount, showModal],
  )

  const verifyReloanPaymentCallback = useCallback(
    async (data) => {
      try {
        const currentAccountDetails = accountList.filter(
          (ele) => ele.accountNo === data.srcAccountNo,
        )[0]

        const featureDetails = featureList['LOAN_PAY']
        if (featureDetails[currentAccountDetails['accountType']] === 'N') {
          setMessage({
            code: t(DIALOG_KEYS.NO_ACCESS),
            message: t(DIALOG_KEYS.NO_ACCOUNTS),
          })
          showModal()
          return
        }

        const currentLoanAccountDetails = loanAccountList.filter(
          (ele) => ele.accountNo === data.loanAccountNo,
        )[0]

        setLoading(true)
        setFormValues(data)

        let reqParams = {
          accountNo: data.srcAccountNo,
          amount: 0,
          brnCode: currentAccountDetails.brnCode,
          currency: currentAccountDetails.currency,
          loanAccBrn: currentLoanAccountDetails.brnCode,
          loanAccountNo: data.loanAccountNo,
        }

        const response = await LoanService.loanRepayVerify(reqParams)

        if (response.status === 200) {
          if (response.data['otpEnable']) {
            setLoading(false)
            setShowAmount(true)
            setTotalLoanAmount(response.data['totalAmountDue'])
            setStage(5)
            return
          }
          reqParams.otp = '0000'
          const confirmResponse = await LoanService.loanRepayConfirm(reqParams)
          if (confirmResponse.status === 200) {
            setLoading(false)
            setMessage(confirmResponse.data.message)
            setStage(4)
          }
        }
      } catch (error) {
        setLoading(false)
      }
    },
    [loanAccountList, accountList, formValues, featureList],
  )

  const confirmLoanRepaymentCallback = useCallback(async () => {
    try {
      setLoading(true)

      const currentAccountDetails = accountList.filter(
        (ele) => ele?.accountNo === formValues.srcAccountNo,
      )[0]
      const currentLoanAccountDetails = loanAccountList.filter(
        (ele) => ele?.accountNo === formValues.loanAccountNo,
      )[0]

      let reqParams = {
        accountNo: formValues.srcAccountNo,
        amount:
          loanFormValues.paymentMode === 'FULL'
            ? loanFormValues.loanAmount
            : +loanFormValues.amount,
        brnCode: currentAccountDetails.brnCode,
        currency: currentAccountDetails.currency,
        loanAccBrn: currentLoanAccountDetails.brnCode,
        loanAccountNo: formValues.loanAccountNo,
        otp: otp,
        paymentMode: loanFormValues.paymentMode,
      }
      const response = await LoanService.loanRepayConfirm(reqParams)
      if (response.status === 200) {
        setLoading(false)
        setMessage(response.data['message'])
        setStage(4)
      }
    } catch (error) {
      setLoading(false)
    }
  }, [loanAccountList, accountList, formValues, otp, loanFormValues])

  function renderSwitch() {
    switch (stage) {
      case 0:
        return renderLoading()
      case 1:
        return renderForm()
      case 2:
        return renderConfirm()
      case 3:
        return (
          <NeoOTP
            otp={otp}
            setOTP={setOTP}
            onSubmit={confirmLoanRepaymentCallback}
            loading={loading}
          />
        )
      case 4:
        return <NeoSuccess message={message} linkPath='/loans' />
      case 5:
        return renderLoanDetails()
      default:
        return renderForm()
    }
  }

  function renderLoading() {
    return (
      <div className='d-flex flex-column text-center justify-content-center align-items-center'>
        <Loading height='50px' width='50px' />
      </div>
    )
  }

  function renderForm() {
    return (
      <div>
        <div className='heading-style'>{t(LOAN_KEYS.NEW_LOAN_KEYS.SUBTITLE_2)}</div>
        <Formik
          initialValues={{
            srcAccountNo: '',
            loanAccountNo: '',
            loanAmount: totalLoanAmount,
            paymentMode: 'FULL',
          }}
          enableReinitialize
          validationSchema={LoanRepaymentSchema}
          onSubmit={verifyReloanPaymentCallback}>
          {({ isSubmitting, values, errors, touched, handleChange, setFieldValue }) => (
            <Form>
              <div className='d-flex row'>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='1'
                    showAsterisk={loanConfig['isSrcAccountNoRequired']}
                    mode='select'
                    text={t(DEPOSITS_KEYS.TERM_DEPOSITS_KEYS.ACCOUNT_NUMBER)}
                    name='srcAccountNo'
                    keyValue={'accountNo'}
                    list={accountList}
                    onChange={handleChange}
                  />
                  <InputError text={errors.srcAccountNo} touched={touched.srcAccountNo} />
                </div>
                <div className='form-group col-12'>
                  <NeoInput
                    tabIndex='2'
                    showAsterisk={loanConfig['isLoanAccountNoRequired']}
                    mode='select'
                    text={t(LOAN_KEYS.NEW_LOAN_KEYS.LOAN_ACCOUNT_NO)}
                    name='loanAccountNo'
                    onChange={handleChange}
                    keyValue={'accountNo'}
                    list={loanAccountList}
                  />
                  <InputError text={errors.loanAccountNo} touched={touched.loanAccountNo} />
                </div>
              </div>

              <div className='mt-3'>
                {isSubmitting ? (
                  renderLoading()
                ) : (
                  <NeoButton
                    tabIndex='3'
                    mode='newDesign'
                    type='submit'
                    text={t(LOAN_KEYS.NEW_LOAN_KEYS.SUBMIT)}
                    disabled={isSubmitting}
                  />
                )}
              </div>
              <div className='back-cancel-btn-container'>
              <button className='back-cancel-btn btn btn-sm new-design-warning-text-color' tabIndex={'4'} onClick={() => history.goBack()}>
                {' '}
                {t(SIDENAV_KEYS.TOUCH_ID_KEYS.CANCEL)}
              </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  function renderLoanDetails() {
    return (
      <div>
        <div>
          <div className='heading-style'>{t(LOAN_KEYS.NEW_LOAN_KEYS.SUBTITLE_3)}</div>
          <Formik
            initialValues={{
              loanAmount: totalLoanAmount,
              paymentMode: 'FULL',
            }}
            enableReinitialize
            validationSchema={''}
            onSubmit={verifyLoanDetailsCallback}>
            {({ isSubmitting, values, errors, touched, handleChange, setFieldValue }) => (
              <Form>
                <div className='d-flex row'>
                  <div className='form-group col-12'>
                    <NeoInput
                      text={t(LOAN_KEYS.NEW_LOAN_KEYS.LOAN_ACCOUNT_NO2)}
                      value={formValues.srcAccountNo}
                      disabled={true}
                    />
                  </div>
                  <div className='form-group col-12'>
                    <NeoInput
                      text={t(LOAN_KEYS.NEW_LOAN_KEYS.LOAN_ACCOUNT_NO1)}
                      value={formValues.loanAccountNo}
                      disabled={true}
                    />
                  </div>
                  <div className='d-flex row'>
                    <div className='col-12'>
                      {showAmount ? (
                        <div className='mt-3 d-flex justify-content-center align-items-center'>
                          <div className='custom-control custom-radio custom-control-inline'>
                            <Field
                              as='input'
                              type='radio'
                              id='fullPayment'
                              name='paymentMode'
                              value='FULL'
                              onChange={() => {
                                setFieldValue('loanAmount', totalLoanAmount)
                                setFieldValue('paymentMode', 'FULL')
                              }}
                              className='custom-control-input'
                            />
                            <label className='custom-control-label' htmlFor='fullPayment'>
                              {t(LOAN_KEYS.NEW_LOAN_KEYS.FULL_PAYMENT)}
                            </label>
                          </div>
                          <div className='custom-control custom-radio custom-control-inline'>
                            <Field
                              as='input'
                              value='PARTIAL'
                              type='radio'
                              id='partialPayment'
                              name='paymentMode'
                              className='custom-control-input'
                              onChange={() => {
                                setFieldValue('loanAmount', '')
                                setFieldValue('paymentMode', 'PARTIAL')
                              }}
                            />
                            <label className='custom-control-label' htmlFor='partialPayment'>
                              {t(LOAN_KEYS.NEW_LOAN_KEYS.PARTIAL)}
                            </label>
                          </div>
                        </div>
                      ) : (
                        <div />
                      )}
                    </div>
                  </div>

                  {showAmount === true ? (
                    <div className='form-group col-12'>
                      <NeoInput
                        text={t(LOAN_KEYS.NEW_LOAN_KEYS.LOAN_AMOUNT)}
                        mode='amount'
                        name='loanAmount'
                        onChange={(values) => {
                          setFieldValue('amount', values.value)
                        }}
                        disabled={values.paymentMode === 'FULL' ? true : false}
                      />
                      <InputError text={errors.loanAmount} touched={touched.loanAmount} />
                    </div>
                  ) : null}
                </div>

                <div className='mt-3'>
                  {isSubmitting ? (
                    renderLoading()
                  ) : (
                    <NeoButton
                      type='submit'
                      mode={'newDesign'}
                      text={t(LOAN_KEYS.NEW_LOAN_KEYS.SUBMIT)}
                      disabled={isSubmitting}
                    />
                  )}
                </div>
                <div className='back-cancel-style' onClick={() => setStage(1)}>
                  {' '}
                  {t(SIDENAV_KEYS.TOUCH_ID_KEYS.BACK)}
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    )
  }

  function renderConfirm() {
    return (
      <div className='d-flex flex-column confirmation'>
        <h3>{t(LOAN_KEYS.NEW_LOAN_KEYS.SUBTITLE)}</h3>
        <div>
          <h6 className='label'>{t(LOAN_KEYS.NEW_LOAN_KEYS.LOAN_ACCOUNT_NO2)}</h6>
          <h6 className='value'>{formValues.srcAccountNo}</h6>
        </div>
        <div>
          <h6 className='label'>{t(LOAN_KEYS.NEW_LOAN_KEYS.LOAN_ACCOUNT_NO1)}</h6>
          <h6 className='value'>{formValues.loanAccountNo}</h6>
        </div>
        <div>
          <h6 className='label'>{t(LOAN_KEYS.NEW_LOAN_KEYS.PAYMENT_MODE)}</h6>
          <h6 className='value'>{loanFormValues.paymentMode}</h6>
        </div>
        <div>
          <h6 className='label'>{t(LOAN_KEYS.NEW_LOAN_KEYS.AMOUNT)}</h6>
          <h6 className='value'>
            <NumberFormat
              value={loanFormValues.loanAmount}
              displayType={'text'}
              thousandSeparator={true}
              prefix={''}
              fixedDecimalScale={true}
            decimalScale={2}
            allowEmptyFormatting
            />
          </h6>
        </div>
        <div>
          <TermsAndConditions module='LOANREPAY' />
        </div>
        <div className='mt-3'>
          {loading ? (
            renderLoading()
          ) : (
            <NeoButton
              type='button'
              mode={'newDesign'}
              text={t(LOAN_KEYS.NEW_LOAN_KEYS.CONFIRM)}
              onClick={() => setStage(3)}
            />
          )}
        </div>
        <div className='back-cancel-style' onClick={() => setStage(1)}>
          {' '}
          {t(SIDENAV_KEYS.TOUCH_ID_KEYS.BACK)}
        </div>
      </div>
    )
  }

  return (
    <LayoutWithOutAccountCard breadCumKey='LOAN_PAYMENT' showEmptySpace={true} keyPath={'LOAN'}>
      <div className='new-design-form-style'>{renderSwitch()}</div>
    </LayoutWithOutAccountCard>
  )
}
